import { Controller } from '@hotwired/stimulus'

const PORCENTAJE = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
const TECLAS = { ArrowLeft: -1, ArrowRight: 1, PageDown: -4, PageUp: 4 }

// El gráfico de la comparativa de ETFs (ENH-RNK-044, `_comparar_evolucion.haml`): una línea vertical en la semana
// más cercana al puntero, un punto en cada línea y una burbuja con la semana y lo que llevaba cada ETF desde el
// principio del periodo. Con el ratón, al tocar o, con el foco en el gráfico, con las flechas, Inicio y Fin; con
// el teclado, además, lo anuncia al lector de pantalla.
//
// Arrastrando de una semana a otra (o con Mayúsculas y las flechas) mide el tramo: la burbuja dice cuánto subió o
// bajó cada ETF entre las dos, de la más antigua a la más reciente, arrastre hacia donde arrastre. El tramo se
// queda al soltar, hasta el siguiente clic o toque, o Escape. Las coordenadas van en porcentaje del lienzo, como
// las del SVG que dibuja `Etfs::EvolucionComparativa`.
export default class extends Controller {
  static targets = ['trazo', 'cursor', 'rango', 'burbuja', 'anuncio']
  static values = { datos: Object }

  connect () {
    this.indice = null
    this.ancla = null
    this.arrastrando = false
    this.puntos = this.creaPuntos()
    this.puntosAncla = this.creaPuntos()
  }

  disconnect () {
    [...this.puntos, ...this.puntosAncla].forEach(punto => punto.remove())
  }

  get ultimo () {
    return this.datosValue.semanas.length - 1
  }

  // Hay tramo cuando las dos puntas no caen en la misma semana.
  get midiendo () {
    return this.ancla !== null && this.ancla !== this.indice
  }

  creaPuntos () {
    return this.datosValue.series.map(serie => {
      const punto = document.createElement('span')
      punto.className = `rnk-Comparativa_Punto rnk-Comparativa_Serie-${serie.serie}`
      punto.hidden = true
      this.trazoTarget.append(punto)
      return punto
    })
  }

  // Al pulsar empieza un tramo; si se suelta en la misma semana, se queda en la semana.
  empieza (evento) {
    if (evento.pointerType === 'mouse' && evento.button !== 0) return

    const indice = this.indiceDe(evento)
    if (indice === null) return

    this.arrastrando = true
    this.ancla = indice
    this.trazoTarget.setPointerCapture?.(evento.pointerId)
    this.muestra(indice)
  }

  mueve (evento) {
    // Con un tramo ya medido, pasar el ratón no lo borra: lo borra el siguiente clic.
    if (!this.arrastrando && this.ancla !== null) return

    const indice = this.indiceDe(evento)
    if (indice !== null) this.muestra(indice)
  }

  suelta () {
    this.arrastrando = false
    if (!this.midiendo) this.ancla = null
  }

  // El navegador se queda el gesto (el dedo hace scroll vertical): no hay tramo.
  cancela () {
    this.arrastrando = false
    this.oculta()
  }

  // Al tocar, la burbuja se queda hasta el siguiente toque: el dedo sale del gráfico al levantarse.
  sale (evento) {
    if (evento.pointerType === 'touch' || this.ancla !== null || this.trazoTarget.matches(':focus-visible')) return

    this.oculta()
  }

  // Solo si se llega con el teclado: tras un clic, el foco no tiene que abrir nada.
  enfoca () {
    if (!this.trazoTarget.matches(':focus-visible')) return

    this.muestra(this.indice ?? this.ultimo, { anuncia: true })
  }

  // Con Mayúsculas, la flecha estira el tramo desde la semana en la que estaba; sin ellas, lo deshace.
  teclea (evento) {
    if (evento.key === 'Escape') return this.oculta()

    const actual = this.indice ?? this.ultimo
    const salto = TECLAS[evento.key]
    let indice
    if (salto) indice = actual + salto
    else if (evento.key === 'Home') indice = 0
    else if (evento.key === 'End') indice = this.ultimo
    else return

    evento.preventDefault()
    if (!evento.shiftKey) this.ancla = null
    else if (this.ancla === null) this.ancla = actual
    this.muestra(indice, { anuncia: true })
  }

  oculta () {
    this.ancla = null
    this.cursorTarget.hidden = true
    this.rangoTarget.hidden = true
    this.burbujaTarget.hidden = true
    ;[...this.puntos, ...this.puntosAncla].forEach(punto => { punto.hidden = true })
  }

  muestra (indice, { anuncia = false } = {}) {
    this.indice = this.limita(indice)
    const x = this.x(this.indice)
    const { semanas, series } = this.datosValue

    this.cursorTarget.style.left = `${x}%`
    this.cursorTarget.hidden = false
    this.colocaPuntos(this.puntos, this.indice)

    let titulo, filas
    if (this.midiendo) {
      const [desde, hasta] = [this.ancla, this.indice].sort((a, b) => a - b)
      this.colocaPuntos(this.puntosAncla, this.ancla)
      this.rangoTarget.style.left = `${this.x(desde)}%`
      this.rangoTarget.style.width = `${this.x(hasta) - this.x(desde)}%`
      this.rangoTarget.hidden = false
      titulo = `Del ${semanas[desde]} al ${semanas[hasta]}`
      filas = series.map(serie => [serie, this.cambio(serie.valores[desde], serie.valores[hasta])])
    } else {
      this.puntosAncla.forEach(punto => { punto.hidden = true })
      this.rangoTarget.hidden = true
      titulo = `Semana del ${semanas[this.indice]}`
      filas = series.map(serie => [serie, this.cambio(100, serie.valores[this.indice])])
    }

    this.pintaBurbuja(titulo, filas, x)
    if (anuncia) {
      const cifras = filas.map(([serie, cambio]) => `${serie.nombre} ${this.porcentaje(cambio)}`).join(', ')
      this.anuncioTarget.textContent = `${titulo}: ${cifras}`
    }
  }

  colocaPuntos (puntos, indice) {
    this.datosValue.series.forEach((serie, posicion) => {
      const valor = serie.valores[indice]
      const punto = puntos[posicion]
      punto.hidden = valor === null
      if (valor === null) return

      punto.style.left = `${this.x(indice)}%`
      punto.style.top = `${this.y(valor)}%`
    })
  }

  pintaBurbuja (titulo, filas, x) {
    const cabecera = document.createElement('p')
    cabecera.className = 'rnk-Comparativa_BurbujaTitulo'
    cabecera.textContent = titulo
    const lista = document.createElement('ul')
    filas.forEach(([serie, cambio]) => {
      const fila = document.createElement('li')
      const muestra = document.createElement('span')
      muestra.className = `rnk-Comparativa_Muestra rnk-Comparativa_Serie-${serie.serie}`
      const nombre = document.createElement('strong')
      nombre.textContent = serie.nombre
      const cifra = document.createElement('span')
      cifra.textContent = this.porcentaje(cambio)
      if (cambio !== null) cifra.className = cambio < 0 ? 'rnk-Comparativa_Baja' : 'rnk-Comparativa_Sube'
      fila.append(muestra, nombre, cifra)
      lista.append(fila)
    })
    this.burbujaTarget.replaceChildren(cabecera, lista)
    // En la mitad derecha, a la izquierda de la línea, para que no se salga del gráfico.
    this.burbujaTarget.style.left = `${x}%`
    this.burbujaTarget.classList.toggle('rnk-Comparativa_Burbuja-izquierda', x > 50)
    this.burbujaTarget.hidden = false
  }

  indiceDe (evento) {
    const caja = this.trazoTarget.getBoundingClientRect()
    if (caja.width === 0) return null

    return this.limita(Math.round((evento.clientX - caja.left) / caja.width * this.ultimo))
  }

  limita (indice) {
    return Math.min(Math.max(indice, 0), this.ultimo)
  }

  // Lo que subió o bajó de un valor a otro, en porcentaje; desde 100 es lo que lleva desde el principio del periodo.
  cambio (desde, hasta) {
    if (desde === null || hasta === null) return null

    return (hasta / desde - 1) * 100
  }

  // «+14,1 %», como las cifras al final de cada línea.
  porcentaje (cambio) {
    if (cambio === null) return '—'

    return `${cambio > 0 ? '+' : ''}${PORCENTAJE.format(cambio)} %`
  }

  x (indice) {
    return this.ultimo > 0 ? indice / this.ultimo * 100 : 0
  }

  // La misma cuenta que `Etfs::EvolucionComparativa#y`.
  y (valor) {
    const { minimo, maximo, margen } = this.datosValue.escala
    const rango = (maximo - minimo) || 1
    return margen + (100 - 2 * margen) * (1 - (valor - minimo) / rango)
  }
}
